import type { CompileOptions, CompileResult, EngineInfo } from './types';

export type EngineStage = 'downloading-runtime' | 'starting-python' | 'installing-engine' | 'ready';

export interface EngineProgress {
  stage: EngineStage;
  /** 0..100 when known. */
  percent?: number;
  /** Technical, untranslated detail (the UI renders a localized label from `stage`). */
  message: string;
}

/**
 * The only way the UI talks to the engine. Level 0 has two implementations (Pyodide worker, mock);
 * level 1 will add an HTTP client behind the same interface.
 */
export interface EngineClient {
  /** Which implementation this is (shown in the UI; never used for behaviour). */
  readonly kind: 'pyodide' | 'mock';
  ready(onProgress?: (p: EngineProgress) => void): Promise<EngineInfo>;
  /**
   * Compiles `text`. Only the latest request matters: a request superseded by a newer one before
   * its result is delivered rejects with `StaleCompileError` (callers simply ignore it).
   */
  compile(text: string, opts?: CompileOptions): Promise<CompileResult>;
  dispose?(): void;
}

/** A newer compile request replaced this one; its result (if any) was dropped. */
export class StaleCompileError extends Error {
  constructor() {
    super('superseded by a newer compile request');
    this.name = 'StaleCompileError';
  }
}

/** The engine could not start. `reason` lets the app decide on a fallback. */
export class EngineUnavailableError extends Error {
  readonly reason: 'wheel-missing' | 'runtime-failed';
  constructor(reason: 'wheel-missing' | 'runtime-failed', message: string) {
    super(message);
    this.name = 'EngineUnavailableError';
    this.reason = reason;
  }
}

export function isStaleCompileError(err: unknown): err is StaleCompileError {
  return err instanceof Error && err.name === 'StaleCompileError';
}

export function isEngineUnavailableError(err: unknown): err is EngineUnavailableError {
  return err instanceof Error && err.name === 'EngineUnavailableError';
}
